import { useState } from "react";
import { ActionIcon, Group, NumberInput, Stack, Text } from "@mantine/core";
import IconCheck from "@tabler/icons-react/dist/esm/icons/IconCheck.mjs";
import IconX from "@tabler/icons-react/dist/esm/icons/IconX.mjs";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  useDeleteLimitMutation,
  usePostLimitMutation,
} from "../../hooks/mutations";
import {
  setEditingLimit,
  clearEditingLimit,
} from "../../../../redux/features/queryBuilder/queryBuilderSlice";

/**
 * Limit section — one number input bound to `query.limit.limit`.
 */
export default function LimitInput({
  ruleId,
  queryId,
  currentLimit,
  canEdit = true,
  isSubquery = false,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { mutate: postLimit } = usePostLimitMutation({ ruleId });
  const { mutate: deleteLimit } = useDeleteLimitMutation({ ruleId });
  const [inputValue, setInputValue] = useState(0);
  const [inputKey, setInputKey] = useState(0);

  const hasLimit = currentLimit !== undefined && currentLimit !== null;
  const showReadOnly = hasLimit || !canEdit;
  const isDirty = Number(inputValue) !== 0;

  const handleCancel = () => {
    setInputValue(0);
    setInputKey((k) => k + 1);
    dispatch(clearEditingLimit());
  };

  const handleSave = () => {
    const limit = Number(inputValue);
    if (Number.isFinite(limit) && limit > 0) {
      postLimit(
        { queryId, limit },
        { onSettled: () => dispatch(clearEditingLimit()) },
      );
    } else {
      dispatch(clearEditingLimit());
    }
  };

  return (
    <Stack gap={4} w={isSubquery ? "100%" : undefined}>
      <Text size="sm" fw={600}>
        {t("dataFields.limit")}
      </Text>
      {showReadOnly ? (
        <Group gap={6} wrap="nowrap" align="center">
          <NumberInput
            value={currentLimit ?? ""}
            readOnly
            variant="filled"
            w={90}
            hideControls
            styles={{ input: { pointerEvents: "none", cursor: "default" } }}
          />
          {canEdit && hasLimit && (
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label="Clear limit"
              onClick={() => deleteLimit({ queryId })}
            >
              <IconX size={16} />
            </ActionIcon>
          )}
        </Group>
      ) : (
        <Group gap={6} wrap="nowrap" align="center">
          <NumberInput
            key={inputKey}
            value={inputValue}
            onChange={setInputValue}
            w={90}
            onFocus={() => dispatch(setEditingLimit(queryId))}
            onBlur={() => {
              if (!isDirty) dispatch(clearEditingLimit());
            }}
            onKeyDown={(ev) => {
              if (ev.key === "Enter") handleSave();
              if (ev.key === "Escape") handleCancel();
            }}
          />
          {isDirty ? (
            <>
              <ActionIcon
                c="gray"
                variant="transparent"
                aria-label="Cancel"
                onClick={handleCancel}
              >
                <IconX size={16} />
              </ActionIcon>
              <ActionIcon
                c="green"
                variant="transparent"
                aria-label="Save limit"
                onClick={handleSave}
              >
                <IconCheck size={16} />
              </ActionIcon>
            </>
          ) : (
            <Text size="sm" c="dimmed">
              (no limit)
            </Text>
          )}
        </Group>
      )}
    </Stack>
  );
}
